'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';

import useAuth from './AuthContext';
import { useSubscriberStatuses } from '@/hooks/useSubscriberStatuses';

export interface UserSubscriptions {
  isActiveSubscriber?: boolean;
  isAgedOutSubscriber?: boolean;
  summary?: string;
}

interface SubscriptionContext {
  data: UserSubscriptions | undefined;
  loading: boolean;
  error: Error | undefined;
}

export const USER_SUBSCRIPTIONS_KEY = 'lovevery_user_subscriptions';

const SubscriptionContext = createContext({} as SubscriptionContext);

const useSubscriptions = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }: any) => {
  const { user } = useAuth();
  const pathname = usePathname();
  const [userSubscriptions, setUserSubscriptions] = useState<
    UserSubscriptions | undefined
  >();

  const userUuid = user?.attributes?.loveveryId;

  // Reset userSubscriptions when the route changes to ensure it gets reloaded
  useEffect(() => {
    const getStoredSubscriptions = Cookies.get(USER_SUBSCRIPTIONS_KEY);
    const storedSubscriptions = getStoredSubscriptions
      ? JSON.parse(getStoredSubscriptions)
      : undefined;
    setUserSubscriptions(storedSubscriptions);
  }, [pathname]);

  const shouldFetch = userUuid && typeof userSubscriptions === 'undefined';

  const {
    data: subscriberStatusData,
    loading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useSubscriberStatuses(shouldFetch ? userUuid : null);

  useEffect(() => {
    if (typeof subscriberStatusData === 'undefined') return;

    const subscriptions = {
      isActiveSubscriber: subscriberStatusData.active,
      isAgedOutSubscriber: subscriberStatusData.aged_out,
      summary: subscriberStatusData.summary_status,
    };

    setUserSubscriptions(subscriptions);

    const isLocalhost =
      typeof window !== 'undefined' && window.location.hostname === 'localhost';

    // Used to exchange information with subdomains
    Cookies.set(USER_SUBSCRIPTIONS_KEY, JSON.stringify(subscriptions), {
      path: '/',
      domain: isLocalhost
        ? 'localhost'
        : (process.env.NEXT_PUBLIC_VANITY_DOMAIN as string),
      sameSite: 'Strict',
      secure: !isLocalhost,
    });
  }, [subscriberStatusData]);

  // Memoize the context value to prevent unnecessary re-renders
  const subscriptionContextValue = useMemo(
    () => ({
      data: userSubscriptions,
      loading: isLoadingSubscriptions,
      error: subscriptionsError ?? undefined,
    }),
    [userSubscriptions, isLoadingSubscriptions, subscriptionsError],
  );

  return (
    <SubscriptionContext.Provider value={subscriptionContextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default useSubscriptions;
